import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, KeyRound } from "lucide-react";

const ROLES = ["MANAGER", "STAFF", "KITCHEN"];
const EMPTY = { name: "", email: "", mobile: "", role: "STAFF", password: "" };

export default function StaffManager() {
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | user object
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => http.get("/admin/staff").then((r) => setStaff(r.data.staff)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(EMPTY); setEditing("new"); };
  const startEdit = (u) => { setForm({ name: u.name, email: u.email, mobile: u.mobile || "", role: u.role, password: "" }); setEditing(u); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    setBusy(true);
    try {
      if (editing === "new") {
        await http.post("/admin/staff", form);
        toast.success("Staff account created");
      } else {
        await http.put(`/admin/staff/${editing.id}`, { name: form.name, email: form.email, mobile: form.mobile, role: form.role });
        toast.success("Staff updated");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await http.put(`/admin/staff/${u.id}`, { active: !(u.active !== false) });
      toast.success(u.active !== false ? "Account deactivated" : "Account activated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.name} (min 8 characters):`);
    if (!pw) return;
    try {
      await http.post(`/admin/staff/${u.id}/reset-password`, { new_password: pw });
      toast.success("Password reset");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="staff-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-serif text-2xl">Team Access ({staff.length})</h3>
        <button data-testid="staff-add-btn" onClick={startNew} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Staff</button>
      </div>

      {editing && (
        <div className="card" data-testid="staff-form">
          <h3 className="font-serif text-2xl mb-4">{editing === "new" ? "Add Staff Account" : `Edit ${editing.name}`}</h3>
          <div className="grid md:grid-cols-3 gap-3">
            <input data-testid="staff-form-name" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="staff-form-email" type="email" placeholder="Login email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="staff-form-mobile" placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            <select data-testid="staff-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
            {editing === "new" && (
              <input data-testid="staff-form-password" type="password" placeholder="Temporary password (min 8) *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            )}
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="staff-form-save" onClick={save} disabled={busy} className="btn-primary">{busy ? "Saving…" : "Save"}</button>
            <button data-testid="staff-form-cancel" onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b" style={{ borderColor: "var(--border)" }}>
            <th className="p-2">Name</th><th>Email</th><th>Mobile</th><th>Role</th><th>Status</th><th>Last login</th><th></th>
          </tr></thead>
          <tbody>
            {staff.map((u) => (
              <tr key={u.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`staff-row-${u.id}`}>
                <td className="p-2 font-medium">{u.name}</td>
                <td>{u.email}</td>
                <td>{u.mobile || "—"}</td>
                <td><span className="badge-pine">{u.role}</span></td>
                <td>{u.role === "SUPER_ADMIN" ? <span className="badge-pine">Owner</span> : (
                  <button data-testid={`staff-active-${u.id}`} onClick={() => toggleActive(u)} className={u.active !== false ? "badge-pine" : "badge-terracotta"}>
                    {u.active !== false ? "Active" : "Inactive"}
                  </button>
                )}</td>
                <td className="text-xs" style={{ color: "var(--muted)" }}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}</td>
                <td className="whitespace-nowrap">
                  {u.role !== "SUPER_ADMIN" && (
                    <>
                      <button data-testid={`staff-edit-${u.id}`} onClick={() => startEdit(u)} className="btn-outline" style={{ padding: "6px 10px", fontSize: 13 }}><Pencil size={13} /></button>
                      <button data-testid={`staff-reset-${u.id}`} onClick={() => resetPw(u)} className="btn-outline ml-1" style={{ padding: "6px 10px", fontSize: 13 }}><KeyRound size={13} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs mt-4" style={{ color: "var(--muted)" }}>
          Staff are deactivated (never deleted) so historical actions stay attributed. The Owner account cannot be edited or deactivated here.
        </p>
      </div>
    </div>
  );
}
